import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { ArrowBack, Close, WarningAmber } from '@mui/icons-material';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    Divider,
    Snackbar,
    Stack,
    styled,
    Tab,
    Tabs,
    Tooltip,
    Typography
} from '@mui/material';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useContainer } from 'unstated-next';

import { LmsSyncDialog } from './LmsSync/LmsSyncDialog';
import { MeetingClaimedModal } from './MeetingClaimedModal';
import { sessionOpened, SessionsTable } from './SessionsTable';
import { SubClassContainer } from './SubClasses/SubClassContainer';
import { SubClassesHub } from './SubClasses/SubClassesHub';
import { type ClassRecording, LmsTypeCapitalized } from './types';
import { CLASS_UPDATE, CREATE_LTI_CODE, UPDATE_CLASS_MEETING } from '../../config/apiPaths';
import { AttendanceProvider, View } from '../../context/AttendanceContext';
import { ClassContext } from '../../context/ClassContext';
import { Roles, type SubClassesResponse } from '../../context/types';
import { useRecordings } from '../../hooks/useRecordings';
import { OrganizationType, useSchoolInfo } from '../../hooks/useSchoolInfo';
import {
    ClassAccess,
    type ClassLevelOptions,
    ClassLevelOverrideOptions,
    type ClassOptionsOverrides,
    type SchoolLevelOptions
} from '../../types/adminConfig';
import { MeetingProvider, Status } from '../../types/apiTypes';
import { copyToClipboard } from '../../utils/clipboard';
import { getUserUuidHeaders } from '../../utils/httpUtils';
import { getUrlParams, getUrls, processApiPath } from '../../utils/url';
import { DownloadChatModal } from '../../views/AdminSettings/components/Classes/DownloadChat/DownloadChatModal';
import { ClassContainer } from '../../views/ClassModal/ClassContainer';
import ClassForm from '../../views/ClassModal/ClassForm';
import AttendanceView from '../attendance/AttendanceView';
import { CollapsibleCard, type CollapsibleCardRow } from '../common/collapsible/CollapsibleCard';
import { DeleteConfirmationDialog } from '../common/dialog/DeleteConfirmationDialog';
import Loadable from '../common/loadable';
import UserState from '../common/userState/UserState';
import AttendanceLayout from '../layout/AttendanceLayout';
import MainLayout, { type NavControlItem, type SubTitleItem } from '../layout/MainLayout';
import { Recordings } from '../recordings/Recordings';
import { TabsPanel } from '../templates/Tables/TemplatesTabsPanel';

const ClassDetailsContainer = styled(Box)`
    margin-bottom: 20px;
` as typeof Box;

const ButtonUnderline = styled(Button)`
    text-decoration: underline;
` as typeof Button;

const ClassroomControls = styled(Stack)`
    flex-direction: row;
    align-items: center;
    gap: 8px;
` as typeof Stack;

const TabContext = styled(Box)`
    width: 100%;
` as typeof Box;

const TabsStyled = styled(Tabs)`
    & .MuiTabs-flexContainer {
        display: flex;
        flex-wrap: wrap;
        flex-direction: row;
        justify-content: space-evenly;
    }

    & .MuiButtonBase-root {
        flex-grow: 1;
        max-width: 600px;
        text-transform: none;
        font-size: 15px;
    }
` as typeof Tabs;

const AttendanceDivider = styled(Divider)`
    margin: 20px 0;
` as typeof Divider;

enum TabNames {
    Upcoming,
    Previous,
    Recordings,
    Attendance
}

const defaultWebExperience = 'nextgen';

const getWebExperience = (
    orgOptions?: SchoolLevelOptions,
    classOptions?: ClassLevelOptions,
    overrides?: ClassOptionsOverrides
) => {
    if (!overrides?.webExperience || overrides.webExperience === ClassLevelOverrideOptions.NoOne) {
        return orgOptions?.webExperience ?? defaultWebExperience;
    }

    return classOptions?.webExperience ?? orgOptions?.webExperience ?? defaultWebExperience;
};

const userTokenLmsTypes: LmsTypeCapitalized[] = [LmsTypeCapitalized.Canvas, LmsTypeCapitalized.D2l];
const subClassLmsSupported: LmsTypeCapitalized[] = [LmsTypeCapitalized.Docebo];

export const ClassroomContent = () => {
    const { enqueueSnackbar } = useSnackbar();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);

    const { recordingUuid } = useParams<{ recordingUuid?: string; action?: string }>();
    const defaultTab = searchParams.get('tab') || recordingUuid ? TabNames.Recordings : TabNames.Upcoming;
    const { api, vanityUrl } = getUrls();
    const history = useHistory();

    const { t } = useTranslation(['class', 'lti', 'common', 'admin_settings']);
    const [isDeleting, setIsDeleting] = useState(false);
    const [tab, setTab] = useState(defaultTab);
    const [showClassEditModal, setShowClassEditModal] = useState(false);
    const [showSubClassModal, setShowSubClassModal] = useState(false);
    const [showDownloadChatModal, setShowDownloadChatModal] = useState(false);
    const [showCopyConfirmation, setShowCopyConfirmation] = useState(false);
    const [showClassDeleteConfirmation, setShowClassDeleteConfirmation] = useState(false);
    const [showLmsSyncModal, setShowLmsSyncModal] = useState(false);

    const [meetingClaimed, setMeetingClaimed] = useState(false);
    const [meetingClaimLoading, setMeetingClaimLoading] = useState(false);

    const [isLaunching, setIsLaunching] = useState(false);

    const [recordings, setRecordings] = useState<ClassRecording[]>([]);

    const { data: school } = useSchoolInfo();

    const {
        me,
        meFetched,
        adminSettings,
        classData,
        fetchClassInfo,
        userUuid,
        classUuid,
        previousSessions,
        upcomingSessions,
        isLoadingClassData,
        classSettings
    } = useContext(ClassContext);

    const { mainClass, subClasses, fetchSubClasses, status: subClassesStatus } = useContainer(SubClassContainer);

    const { class: classInfo, classGroups, userGroups, lead_instructor: leadInstructor } = classData;

    const {
        lti_uuid: ltiUuid,
        is_guest: isGuestParam,
        from_scheduler: fromScheduler,
        from_teams: fromTeams,
        legacyRecordings,
        skipHub
    } = getUrlParams();

    const isGuest = isGuestParam === 'true';

    const { role, user_name: username } = me;
    const isInstructor = useMemo(() => role === Roles.Teacher, [role]);
    const isAssistant = useMemo(() => role === Roles.Assistant, [role]);
    const isPrivilegedUser = useMemo(() => isInstructor || isAssistant, [isAssistant, isInstructor]);
    const {
        sid: classId,
        class_name: className,
        meeting_id: meetingId,
        password: passcode,
        meeting_provider: meetingProvider,
        lms_sync_settings: lmsSyncSettings,
        lms_type: lmsType,
        parent: parentClassId
    } = classInfo;
    const classNameStr = className || 'unknown';
    const isZoomProvider = meetingProvider && meetingProvider.trim().toLowerCase() === 'zoom';
    const canLmsSync =
        (subClassLmsSupported.includes(lmsType as LmsTypeCapitalized) || !parentClassId) &&
        isInstructor &&
        lmsSyncSettings?.sync_enabled &&
        lmsType;

    const canDeleteClass = useMemo(() => {
        if (!classData.class) {
            return false;
        }
        // Only allow deletion of classes that are not sub classes and are not created via LTI
        return isPrivilegedUser && fromScheduler && !classData.class.parent && !classData.class.lti_state;
    }, [classData.class, fromScheduler, isPrivilegedUser]);

    const subClassesEnabled = useMemo(() => adminSettings?.options.subClasses, [adminSettings?.options.subClasses]);
    const missingTokenWarning = useMemo(
        () => userTokenLmsTypes.includes(lmsType as LmsTypeCapitalized) && !lmsSyncSettings?.has_api_token,
        [lmsType, lmsSyncSettings]
    );

    // Super admin setting for when these tabs a visible to users
    const canAccess = useCallback(
        (access: ClassAccess | undefined): boolean => {
            if (!access || access === ClassAccess.None) {
                return false;
            }

            if (access === ClassAccess.All) {
                return true;
            }

            return fromScheduler ? access === ClassAccess.App : access === ClassAccess.Lti;
        },
        [fromScheduler]
    );

    const canSeeRecordings =
        (isPrivilegedUser && fromScheduler === '2') ||
        ((isPrivilegedUser || adminSettings?.recording_participants_can_view) &&
            canAccess(adminSettings?.recording_tab_access));

    const canSeeAttendance = isPrivilegedUser && canAccess(adminSettings?.attendance_tab_access);

    const handleSettingsAndScheduleOpen = () => {
        setShowClassEditModal(true);
    };

    const handleSettingsAndScheduleClose = () => {
        setShowClassEditModal(false);
    };

    const handleClassUpdate = async () => {
        fetchClassInfo();
        handleSettingsAndScheduleClose();
    };

    const handleLmsSyncOpen = useCallback(() => {
        if (!lmsType) {
            console.error('No LMS type found');
            return;
        }
        setShowLmsSyncModal(true);
    }, [lmsType]);

    const handleLmsSyncComplete = async () => {
        await Promise.all([
            fetchClassInfo(),
            (async () => {
                if (subClassesEnabled) {
                    await fetchSubClasses(true);
                }
            })()
        ]);
    };

    const handleClaimMeeting = useCallback(async () => {
        setMeetingClaimLoading(true);

        try {
            await axios.post(
                `${api}${UPDATE_CLASS_MEETING}`,
                { class_uuid: classUuid },
                { headers: getUserUuidHeaders(userUuid) }
            );

            /**
             * Only auto-refresh if outside LTI context
             */
            if (fromScheduler) {
                window.location.reload();
                return;
            }

            /**
             * Force user to refresh/relaunch LMS manually
             * to get a new verification code
             */
            setMeetingClaimed(true);
        } catch (err) {
            enqueueSnackbar(t('class:error-message'), { variant: 'error' });
        }

        setMeetingClaimLoading(false);
    }, [api, classUuid, enqueueSnackbar, fromScheduler, t, userUuid]);

    const handleRosterOpen = useCallback(() => {
        history.push(`/lti/${classUuid}/roster${window.location.search}`);
    }, [classUuid, history]);

    const handleDownloadChatsOpen = () => {
        setShowDownloadChatModal(true);
    };

    const handleDownloadChatsClose = () => {
        setShowDownloadChatModal(false);
    };

    const closeSchedulerClassroomPage = useCallback(() => {
        if (!fromScheduler) {
            return;
        }

        window.parent.postMessage(
            {
                func: 'request_close_classroom',
                event: {
                    meeting_id: meetingId
                }
            },
            '*'
        );
    }, [fromScheduler, meetingId]);

    const handleClassDelete = async () => {
        if (!meetingId || !userUuid || !fromScheduler) {
            return;
        }

        setIsDeleting(true);

        try {
            await axios.delete(processApiPath(CLASS_UPDATE, undefined, { meeting_id: meetingId, user_uuid: userUuid }));
            closeSchedulerClassroomPage();
        } catch (reason) {
            console.error(reason);
            setShowClassDeleteConfirmation(false);
        }

        setIsDeleting(false);
    };

    const handleClassDeleteConfirmationClose = () => {
        if (isDeleting) {
            return;
        }

        setShowClassDeleteConfirmation(false);
    };

    const controls = useMemo(() => {
        if (isLoadingClassData) {
            return [];
        }

        const newControls: NavControlItem[] = [];
        const burgerMenuItems: NavControlItem[] = [];

        if (isInstructor || isAssistant) {
            if (fromScheduler) {
                burgerMenuItems.push({
                    label: t('roster:roster-title'),
                    dataTestId: 'button-roster',
                    onClick: handleRosterOpen
                });
            }

            if (adminSettings?.instructor_chat_download) {
                burgerMenuItems.push({
                    label: t('admin_settings:classes.download-chat'),
                    dataTestId: 'button-download-chats',
                    onClick: handleDownloadChatsOpen
                });
            }
        }

        if (isInstructor && meFetched) {
            if (canLmsSync) {
                newControls.push({
                    label: t('lti:sync-lms-data'),
                    dataTestId: 'button-sync-lms-data',
                    onClick: handleLmsSyncOpen,
                    icon: missingTokenWarning ? (
                        <Tooltip title={t('lms_sync:warning.missing-token-warning')}>
                            <WarningAmber sx={{ color: 'warning.light' }} />
                        </Tooltip>
                    ) : undefined
                });
            }

            if (
                (school?.type === OrganizationType.Collab || school?.type === OrganizationType.User) &&
                me.email !== leadInstructor?.email &&
                adminSettings?.allow_instructor_swap
            ) {
                newControls.push({
                    label: meetingClaimLoading ? t('lti:create-new-meeting-loading') : t('lti:create-new-meeting'),
                    dataTestId: 'create-new-meeting',
                    onClick: handleClaimMeeting,
                    disabled: meetingClaimLoading
                });
            }

            newControls.push({
                label: t('lti:button-settings-and-schedule'),
                dataTestId: 'button-settings-and-schedule',
                onClick: handleSettingsAndScheduleOpen
            });

            if (canDeleteClass) {
                burgerMenuItems.push({
                    label: t('common:delete'),
                    dataTestId: 'button-delete-class',
                    color: 'error',
                    section: 1,
                    onClick: () => setShowClassDeleteConfirmation(true)
                });
            }
        }

        if (burgerMenuItems.length) {
            newControls.push({
                label: '',
                menu: burgerMenuItems,
                dataTestId: 'button-burger-menu'
            });
        }
        if (meFetched) {
            newControls.push(
                <UserState key="menu-user-state" name={me.user_name} email={me.email} avatarUrl={me.user_image} />
            );
        }

        if (fromScheduler && !fromTeams) {
            newControls.push({
                label: '',
                dataTestId: 'button-close',
                icon: <Close />,
                onClick: closeSchedulerClassroomPage
            });
        }

        return newControls;
    }, [
        isLoadingClassData,
        isInstructor,
        isAssistant,
        meFetched,
        fromScheduler,
        fromTeams,
        adminSettings?.instructor_chat_download,
        adminSettings?.allow_instructor_swap,
        t,
        handleRosterOpen,
        canLmsSync,
        school?.type,
        me.email,
        me.user_name,
        me.user_image,
        leadInstructor?.email,
        canDeleteClass,
        handleLmsSyncOpen,
        missingTokenWarning,
        meetingClaimLoading,
        handleClaimMeeting,
        closeSchedulerClassroomPage
    ]);

    const detailsCardRows = useMemo(() => {
        const rows: CollapsibleCardRow[] = [];
        const hostName: string = [classInfo.first_name ?? '', classInfo.last_name ?? ''].join(' ');

        const isTeams = classInfo.meeting_provider === MeetingProvider.ACS;

        if (classInfo.class_description) {
            rows.push({ key: t('lti:description'), value: classInfo.class_description });
        }

        if (isPrivilegedUser || adminSettings?.show_meeting_info) {
            if (hostName && !isTeams) {
                rows.push({ key: t('lti:host'), value: hostName });
            }
            rows.push({ key: t('lti:meeting-id'), value: meetingId });
            if (Number(meetingId) > 0 && passcode) {
                rows.push({ key: t('lti:passcode'), value: passcode });
            }
        }

        return rows;
    }, [
        adminSettings?.show_meeting_info,
        classInfo.class_description,
        classInfo.first_name,
        classInfo.last_name,
        classInfo.meeting_provider,
        isPrivilegedUser,
        meetingId,
        passcode,
        t
    ]);

    const onClickLaunch = async () => {
        const webExperience = getWebExperience(
            adminSettings?.options,
            classData.class.class_options,
            adminSettings?.overrides ?? {}
        );
        if (fromScheduler) {
            // This page was accessed via the scheduler, so we need to launch differently.
            // Pass a request to join the class up to the parent window.
            window.parent.postMessage(
                {
                    func: 'request_join_class',
                    event: {
                        meeting_id: meetingId,
                        password: passcode,
                        user_uuid: userUuid,
                        meeting_provider: meetingProvider,
                        webExperience,
                        username
                    }
                },
                '*'
            );

            return;
        }

        setIsLaunching(true);

        try {
            const response = await axios.post(
                `${api}${CREATE_LTI_CODE}`,
                { student_id: me.student_id },
                { headers: { token: userUuid } }
            );

            const ltiCode = response.data.code;
            let url = `${window.location.protocol}//${window.location.host}/class/${classUuid}`;

            const params = new URLSearchParams({
                code: ltiCode,
                is_lti: 'true',
                username: username ?? ''
            });
            url += `?${params}`;

            window.open(url, 'Class');
        } catch (err) {
            console.error(err);
            enqueueSnackbar(t('class:error-launch'), { variant: 'error' });
        }

        setIsLaunching(false);
    };

    const onClickCopyInviteLink = async () => {
        await copyToClipboard(`${vanityUrl}/class/${classUuid}`);
        setShowCopyConfirmation(true);
    };

    const onCloseCopyLinkClickHandler = () => {
        setShowCopyConfirmation(false);
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTab(newValue);
    };

    const showJoinClassRoomButton = useMemo(() => {
        if (isGuest) {
            return false;
        }
        if (isPrivilegedUser) {
            return true;
        }
        if (sessionOpened(classSettings, isPrivilegedUser, upcomingSessions?.[0])) {
            return true;
        }

        switch (adminSettings?.class_access) {
            case ClassAccess.All:
                return true;
            case ClassAccess.App:
                return fromScheduler;
            case ClassAccess.Lti:
                return !fromScheduler;
            default:
                return false;
        }
    }, [adminSettings?.class_access, classSettings, fromScheduler, isGuest, isPrivilegedUser, upcomingSessions]);

    const restrictSessionsRecordingsByGroups =
        !isPrivilegedUser && !!classGroups?.length && !!adminSettings?.restrict_sessions_recordings_by_groups;

    const showLegacyRecordings = Boolean(
        isZoomProvider && (legacyRecordings || !adminSettings?.recording_interactive_player)
    );

    const { refetchRecordings, data, isLoadingRecordings, isFetchingRecordings } = useRecordings({
        userUuid,
        classUuid,
        meetingId,
        isPrivilegedUser: isInstructor,
        restrictRecordingsByGroups: restrictSessionsRecordingsByGroups,
        previousSessions,
        upcomingSessions,
        classGroups,
        showLegacyRecordings,
        doFetch: tab == TabNames.Recordings
    });

    const disableMainClass = useMemo(
        () => lmsType === LmsTypeCapitalized.Docebo || (!isPrivilegedUser && !!classData.class.data?.locked),
        [lmsType, isPrivilegedUser, classData.class.data?.locked]
    );

    const disableSubClassEdit = useMemo(() => lmsType === LmsTypeCapitalized.Docebo, [lmsType]);

    useEffect(() => {
        setRecordings(data ?? []);
    }, [setRecordings, data]);

    useEffect(() => {
        if (subClassesEnabled && subClassesStatus === Status.NotStarted) {
            fetchSubClasses().then((response?: SubClassesResponse) => {
                // If there are sub classes, show the Hub. If there are no sub classes, show the main class, unless the main class is disabled.
                if ((!parentClassId && disableMainClass) || (response?.subClasses?.length && !skipHub)) {
                    setShowSubClassModal(true);
                }
            });
        }
    }, [disableMainClass, fetchSubClasses, parentClassId, skipHub, subClassesEnabled, subClassesStatus]);

    const navigateToHub: boolean = useMemo(
        () => Boolean(subClassesEnabled && (isPrivilegedUser || subClasses?.length)),
        [subClassesEnabled, isPrivilegedUser, subClasses]
    );

    const subTitles: SubTitleItem[] = useMemo(() => {
        const result: SubTitleItem[] = [];

        if (mainClass && classUuid !== mainClass.class_uuid) {
            result.push({
                title: classInfo.class_name
            });
        }

        if (navigateToHub) {
            result.push({
                title: t('class:back-to-hub'),
                icon: <ArrowBack fontSize="small" />,
                sx: { fontSize: '16px' }
            });
        }
        return result;
    }, [classInfo.class_name, classUuid, mainClass, navigateToHub, t]);

    return (
        <Loadable
            fullScreen={false}
            isLoading={isLoadingClassData || (subClassesEnabled && subClassesStatus !== Status.Complete)}
        >
            {showSubClassModal ? (
                <SubClassesHub
                    open={showSubClassModal}
                    onClose={() => setShowSubClassModal(false)}
                    disableMainClass={disableMainClass}
                    disableEdit={disableSubClassEdit}
                />
            ) : (
                <MainLayout
                    title={mainClass?.class_name || classNameStr}
                    titleOnClick={navigateToHub ? () => setShowSubClassModal(true) : undefined}
                    subTitles={subTitles}
                    controls={controls}
                    narrowStyle={fromScheduler === '1'}
                >
                    <ClassContainer.Provider initialState={{ classData }}>
                        <Dialog
                            open={showClassEditModal}
                            onClose={handleSettingsAndScheduleClose}
                            fullScreen={Boolean(fromScheduler)}
                        >
                            <ClassForm onClose={handleSettingsAndScheduleClose} onSuccess={handleClassUpdate} />
                        </Dialog>
                    </ClassContainer.Provider>

                    {canDeleteClass && (
                        <DeleteConfirmationDialog
                            title={t('class_schedule:title.delete-class')}
                            open={showClassDeleteConfirmation}
                            onConfirm={handleClassDelete}
                            onClose={handleClassDeleteConfirmationClose}
                        >
                            <Typography>{t('class_schedule:delete-confirm')}</Typography>
                        </DeleteConfirmationDialog>
                    )}

                    <LmsSyncDialog
                        open={showLmsSyncModal}
                        onComplete={handleLmsSyncComplete}
                        onClose={() => setShowLmsSyncModal(false)}
                        lmsSyncSettings={lmsSyncSettings}
                        ltiUuid={ltiUuid!}
                        lmsType={lmsType!}
                        userUuid={userUuid}
                        classId={String(classId)}
                        classUuid={classUuid}
                    />

                    {!isGuest && (
                        <ClassDetailsContainer>
                            <ClassroomControls>
                                {showJoinClassRoomButton && (
                                    <Button
                                        size="medium"
                                        color="primary"
                                        variant="contained"
                                        disabled={!meFetched}
                                        onClick={onClickLaunch}
                                    >
                                        {isLaunching ? t('class:launching') : t('lti:join-class-room')}
                                    </Button>
                                )}

                                {isInstructor && (
                                    <>
                                        <ButtonUnderline
                                            variant="text"
                                            disabled={!classUuid}
                                            onClick={onClickCopyInviteLink}
                                        >
                                            {t('lti:copy-guest-invite-link')}
                                        </ButtonUnderline>
                                        <Snackbar
                                            open={showCopyConfirmation}
                                            autoHideDuration={3000}
                                            onClose={onCloseCopyLinkClickHandler}
                                            message={t('common:copied')}
                                        />
                                    </>
                                )}
                            </ClassroomControls>

                            {detailsCardRows.length > 0 && (
                                <CollapsibleCard
                                    id="advanced-section"
                                    title={t('lti:details')}
                                    rows={detailsCardRows}
                                />
                            )}
                        </ClassDetailsContainer>
                    )}

                    <TabContext>
                        <TabsStyled value={tab} onChange={handleTabChange}>
                            <Tab value={TabNames.Upcoming} label={t('class:upcoming')} />
                            <Tab value={TabNames.Previous} label={t('class:previous')} />

                            {canSeeRecordings && (
                                <Tab
                                    value={TabNames.Recordings}
                                    key="recordings-tab"
                                    label={
                                        <Box position="relative">
                                            {t('class:recordings')}
                                            {tab === TabNames.Recordings && isFetchingRecordings && (
                                                <CircularProgress
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 'calc(50% - 8px)',
                                                        right: '-20px'
                                                    }}
                                                    size={16}
                                                />
                                            )}
                                        </Box>
                                    }
                                />
                            )}

                            {canSeeAttendance && (
                                <Tab value={TabNames.Attendance} key="attendance-tab" label={t('class:attendance')} />
                            )}
                        </TabsStyled>

                        <TabsPanel value={tab} index={TabNames.Upcoming}>
                            {userGroups && classGroups && (
                                <SessionsTable
                                    id="upcoming-sessions-table"
                                    role={me.role}
                                    userGroups={userGroups}
                                    classGroups={classGroups}
                                    sessions={upcomingSessions}
                                    className={classNameStr}
                                    isInstructor={isInstructor}
                                    isGuest={isGuest}
                                    showSections={!fromScheduler}
                                    onClickSchedule={handleSettingsAndScheduleOpen}
                                    onClickLaunch={onClickLaunch}
                                    isPrivilegedUser={isPrivilegedUser}
                                    classSettings={classSettings}
                                />
                            )}
                        </TabsPanel>

                        <TabsPanel value={tab} index={TabNames.Previous}>
                            {userGroups && classGroups && (
                                <SessionsTable
                                    id="previous-sessions-table"
                                    role={me.role}
                                    userGroups={userGroups}
                                    classGroups={classGroups}
                                    sessions={previousSessions}
                                    className={classNameStr}
                                    isInstructor={isInstructor}
                                    isGuest={isGuest}
                                    showSections={!fromScheduler}
                                    showingPastSessionsOnly={true}
                                    isPrivilegedUser={isPrivilegedUser}
                                    classSettings={classSettings}
                                />
                            )}
                        </TabsPanel>

                        {
                            // See previous comment about fromScheduler !== 1 in tab logic.
                        }
                        {canSeeRecordings && (
                            <TabsPanel key="recordings-tabpanel" value={tab} index={TabNames.Recordings}>
                                <Recordings
                                    userUuid={userUuid}
                                    classUuid={classUuid}
                                    isPrivilegedUser={isInstructor}
                                    recordings={recordings}
                                    setRecordings={setRecordings}
                                    showLegacyRecordings={showLegacyRecordings}
                                    isFetchingRecordings={isFetchingRecordings}
                                    isLoadingRecordings={isLoadingRecordings}
                                    refetchRecordings={refetchRecordings}
                                />
                            </TabsPanel>
                        )}

                        {canSeeAttendance && (
                            <TabsPanel key="attendance-tabpanel" value={tab} index={TabNames.Attendance}>
                                <AttendanceDivider />
                                {!classInfo?.class_uuid && <CircularProgress />}

                                {classInfo?.class_uuid && (
                                    <AttendanceProvider
                                        classId={classInfo.class_uuid}
                                        classSessionId="session-summary"
                                        view={View.SessionSummary}
                                    >
                                        <AttendanceLayout>
                                            <AttendanceView />
                                        </AttendanceLayout>
                                    </AttendanceProvider>
                                )}
                            </TabsPanel>
                        )}
                    </TabContext>

                    <DownloadChatModal
                        onClose={handleDownloadChatsClose}
                        open={showDownloadChatModal}
                        classId={classId}
                        className={classNameStr}
                        userUuid={userUuid}
                        classUuid={classUuid}
                    />

                    {meetingClaimed && <MeetingClaimedModal />}
                </MainLayout>
            )}
        </Loadable>
    );
};
